'use client'

import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'

const checkboxVariants = cva(
  'peer shrink-0 border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
  {
    variants: {
      size: {
        default: 'h-4 w-4 rounded-[4px]',
        // Tablet line control (cafeteria serve, PROD-1278): a 32px box a
        // cafeteria worker can hit with a thumb between trays.
        lg: 'size-8 rounded-lg',
      },
    },
    defaultVariants: { size: 'default' },
  },
)

const checkboxGlyphVariants = cva('', {
  variants: {
    size: {
      default: 'h-3 w-3',
      lg: 'size-6',
    },
  },
  defaultVariants: { size: 'default' },
})

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, VariantProps<typeof checkboxVariants> {}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, size, ...props }, ref) => (
    <CheckboxPrimitive.Root ref={ref} className={cn(checkboxVariants({ size }), className)} {...props}>
      {/* The glyph must be smaller than the 16px box and stroked heavier than
          lucide's default 2 — a full-size thin check has no margin and reads
          as a solid filled square on small/high-DPI phone screens (chefs on
          Galaxy tablets could not tell checked from unchecked). The lg box
          keeps the same margin ratio with a slightly lighter stroke. */}
      <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
        <Check className={checkboxGlyphVariants({ size })} strokeWidth={size === 'lg' ? 3 : 3.5} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
)
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox, checkboxVariants }
